import { Box, Card, Typography } from '@mui/material';
import * as d3 from 'd3';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import useResizeObserver from 'use-resize-observer';

const height = 350;
const margin = {
    bottom: 20,
    left: 50,
    right: 0,
    top: 10,
};

export default function Chart({ axes, children, legend, title, scaleX, scaleY, onRectChange }: ChartProps) {
    const [node, setNode] = useState(d3.select(null));
    const containerObserver = useResizeObserver<HTMLDivElement>();

    const canvasRef = useCallback((ref: any) => {
        const newNode = d3.select(ref);
        setNode(newNode);
    }, []);

    const chartRect = useMemo(() => {
        return new DOMRect(
            margin.left,
            margin.top,
            Math.max(0, (containerObserver.width || 0) - margin.left - margin.right),
            height - margin.top - margin.bottom
        );
    }, [containerObserver.width]);

    useEffect(() => {
        onRectChange(chartRect);
    }, [chartRect, onRectChange]);

    useEffect(() => {
        // Empty axes container
        const container = node.select('.chart-axes');
        container.selectChildren().remove();

        // Add X axis
        const axisX = d3.axisBottom(scaleX).tickPadding(10).tickSizeInner(0).tickSizeOuter(0);

        container
            .append('g')
            .call(axisX)
            .attr('font-family', null)
            .attr('font-size', null)
            .attr('transform', `translate(${chartRect.left}, ${chartRect.bottom})`);

        // Add Y axis
        const axisY = d3
            .axisLeft(scaleY)
            .tickFormat(d => `${axes.prefixY}${d.valueOf().toLocaleString()}${axes.suffixY}`)
            .tickPadding(10)
            .tickSizeInner(0)
            .tickSizeOuter(0);

        container
            .append('g')
            .call(axisY)
            .attr('font-family', null)
            .attr('font-size', null)
            .attr('transform', `translate(${chartRect.left}, ${chartRect.top})`);

        container
            .append('g')
            .selectAll('line')
            .data(scaleY.ticks(axisY.tickArguments()[0]))
            .enter()
            .append('line')
            .attr('x1', chartRect.left)
            .attr('x2', chartRect.left + chartRect.width)
            .attr('y1', d => scaleY(d) + chartRect.top)
            .attr('y2', d => scaleY(d) + chartRect.top)
            .attr('stroke', '#EDEFF5')
            .attr('stroke-width', 0.5);

        // Style ticks
        container.selectAll('.tick').style('color', '#949494');
    }, [axes, chartRect, node, scaleX, scaleY]);

    return (
        <Box width="100%">
            <Card
                sx={{
                    border: 8,
                    borderColor: '#5D9562',
                    borderRadius: 5,
                }}
            >
                <Typography component="div" padding={3} textTransform="uppercase" variant="roboto511">
                    <Box display="flex" justifyContent="space-between" marginBottom={3}>
                        <Box>{title}</Box>
                        {legend && (
                            <Box display="flex" gap={3}>
                                {legend.map((item, index) => (
                                    <Box alignItems="center" display="flex" gap={1} key={index}>
                                        <Box bgcolor={item.color} width={12} height={3} />
                                        <Box>{item.label}</Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <Box>{axes.labelY}</Box>
                    <Box ref={containerObserver.ref}>
                        <svg
                            height={height}
                            ref={canvasRef}
                            viewBox={`0 0 ${containerObserver.width || 0} ${height}`}
                            width="100%"
                        >
                            <g className="chart-axes" />
                            <g transform={`translate(${chartRect.left}, ${chartRect.top})`}>{children}</g>
                        </svg>
                    </Box>
                    <Box textAlign="right">{axes.labelX}</Box>
                </Typography>
            </Card>

            <Tooltip
                arrowColor="#C4C4C4"
                id="chart-tooltip"
                place="right"
                style={{
                    backgroundColor: '#C4C4C4',
                    color: '#000000',
                }}
            />
        </Box>
    );
}
